import type { Handle } from 'remix/ui'
import { on, ref } from 'remix/ui'
import { BlobImage } from '../components/blob-image'
import { BrandMark } from '../components/brand-mark'
import { ConfirmSheet } from '../components/confirm-sheet'
import { HomeOptionsSheet } from '../components/home-options-sheet'
import { IconMore, IconPhone, IconPlus, IconSettings, IconWide } from '../components/icons'
import { RenameSheet } from '../components/rename-sheet'
import { StorageMeter } from '../components/storage-meter'
import { downloadBlob, shareOrDownload } from '../lib/media'
import { loadHomePage, type HomeLoaderData, type ProjectSummary } from '../lib/project-actions'
import {
  BACKUP_FILE_ACCEPT,
  BACKUP_IMPORT_READING,
  BackupFormatError,
  backupImportErrorMessage,
  backupImportProgressLabel,
  importKodyVideoBackupFile,
  projectBackupFilename,
  serializeProject,
} from '../lib/project-transfer'
import {
  deleteProject,
  getClipsForProject,
  getProjectAudio,
  getProjectText,
  renameProject,
} from '../lib/storage'
import { clearExportCache } from '../lib/export/export-cache'
import { reportError } from '../lib/error-reporting'
import { navigate, onNavigate } from '../router'
import {
  layoutToggleApplies,
  readLayoutMode,
  toggleLayoutMode,
  type LayoutMode,
} from '../lib/layout-mode'
import { formatBytes, formatStoragePercent, storageSeverity } from '../lib/storage-space'
import {
  NEW_PROJECT_ID,
  PROJECT_KINDS,
  PROJECT_KIND_LABELS,
  formatDuration,
  maxProjectsFor,
  projectKindFromSearch,
  type ClipRecord,
  type ProjectAudioRecord,
  type ProjectKind,
} from '../lib/types'

/** Android share targets get flaky well below this; bigger backups download. */
const SHARE_BACKUP_LIMIT_BYTES = 50 * 1024 * 1024

/**
 * Last loaded home data, kept across mounts: navigating back to home renders
 * the cards immediately (like React Router's blocking loader used to) while
 * a fresh load revalidates underneath. Keyed by tab, so switching back to a
 * tab you have already seen is instant too.
 */
const lastHomeData = new Map<ProjectKind, HomeLoaderData>()

/** The tab in the URL (`/?kind=voice`), defaulting to Video. */
function kindFromLocation(): ProjectKind {
  return projectKindFromSearch(window.location.search)
}

/** Empty-state copy per tab. */
const EMPTY_COPY: Record<ProjectKind, { title: string; hint: string }> = {
  video: {
    title: 'New project',
    hint: 'Hold to record clips, then publish the film.',
  },
  voice: {
    title: 'New recording',
    hint: 'Dictate in takes; they join into one audio file.',
  },
  text: {
    title: 'New note',
    hint: 'Write markdown and publish it straight to a feed.',
  },
}

export function HomePage(handle: Handle) {
  let kind = kindFromLocation()
  let data: HomeLoaderData | null = lastHomeData.get(kind) ?? null
  let error: string | null = null
  let menuProject: ProjectSummary | null = null
  let renaming: ProjectSummary | null = null
  let deleting: ProjectSummary | null = null
  let busy = false
  let notice: string | null = null
  /** Restoring a backup: the progress line, then cleared. */
  let restoring: string | null = null
  let restoreError: string | null = null
  const backupInputRef: { current: HTMLInputElement | null } = { current: null }
  let layout: LayoutMode = readLayoutMode()
  /** Only offered on a desktop-sized window; a phone is already phone-shaped. */
  const showLayoutToggle = layoutToggleApplies()
  // Prefetched when the options sheet opens so the Save-backup tap keeps its
  // user activation (Web Share needs it; an IndexedDB read can outlive it).
  let prefetchedClips: {
    projectId: string
    clips: Promise<ClipRecord[]>
    audio: Promise<ProjectAudioRecord | undefined>
  } | null = null

  /** Monotonic request id: an older in-flight load (e.g. the mount load
   * resolving after a delete's refresh) must never overwrite newer state. */
  let refreshVersion = 0
  const refresh = () => {
    const version = ++refreshVersion
    const forKind = kind
    void loadHomePage(forKind)
      .then((loaded) => {
        if (handle.signal.aborted || version !== refreshVersion) return
        lastHomeData.set(forKind, loaded)
        data = loaded
        error = null
        void handle.update()
      })
      .catch((err) => {
        if (handle.signal.aborted || version !== refreshVersion) return
        reportError(err, 'load-home')
        error = err instanceof Error ? err.message : 'Could not load your projects.'
        void handle.update()
      })
  }
  refresh()

  // The tab lives in the URL, so Back returns to the tab you came from and a
  // link can point straight at one.
  onNavigate(handle, () => {
    const next = kindFromLocation()
    if (next === kind) return
    kind = next
    data = lastHomeData.get(kind) ?? null
    notice = null
    void handle.update()
    refresh()
  })

  const selectKind = (next: ProjectKind) => {
    if (next === kind) return
    navigate(next === 'video' ? '/' : `/?kind=${next}`)
  }

  // Nothing is persisted until the first take/keystroke — backing out of an
  // untouched new project leaves no empty project behind.
  /**
   * Restore a project from a `.okbvideo` backup.
   *
   * This path already existed — but only as a drag-and-drop target, which is
   * unusable on a phone and undiscoverable on a desktop. Same import underneath;
   * the only new thing is a way to reach it.
   *
   * The picked file is imported WHATEVER it is called. `accept` is advisory, and
   * a backup that came off a phone through a share sheet or a download often
   * arrives renamed — refusing it on the extension would look like the feature
   * being broken. A file that is not a backup fails in `parseProjectBackup` with
   * a message written for the user.
   */
  const restoreFromBackup = (file: File) => {
    if (restoring !== null) return
    restoreError = null
    restoring = BACKUP_IMPORT_READING
    void handle.update()
    void (async () => {
      try {
        const project = await importKodyVideoBackupFile(file, (done, total) => {
          restoring = backupImportProgressLabel(done, total)
          void handle.update()
        })
        navigate(`/project/${project.id}`)
      } catch (err) {
        if (!(err instanceof BackupFormatError)) reportError(err, 'import-picker')
        restoreError = backupImportErrorMessage(err)
      } finally {
        restoring = null
        void handle.update()
      }
    })()
  }

  const openNewProject = () => {
    navigate(kind === 'video' ? `/project/${NEW_PROJECT_ID}` : `/project/${NEW_PROJECT_ID}?kind=${kind}`)
  }

  // Cached export files (the recoverable last export + scratch) can hold
  // gigabytes — when storage runs hot, the fix must be one tap away.
  const onClearExportCache = () => {
    busy = true
    error = null
    notice = null
    void handle.update()
    void clearExportCache()
      .then((freedBytes) => {
        notice = `Cleared cached export files — freed ${formatBytes(freedBytes)}.`
        refresh()
      })
      .catch((err) => {
        reportError(err, 'clear-export-cache')
        error = err instanceof Error ? err.message : 'Could not clear cached exports — try again.'
      })
      .finally(() => {
        busy = false
        void handle.update()
      })
  }

  const backupProject = (project: ProjectSummary) => {
    void (async () => {
      busy = true
      error = null
      notice = null
      void handle.update()
      try {
        const prefetched = prefetchedClips
        const usePrefetch = prefetched !== null && prefetched.projectId === project.id
        const clips = usePrefetch
          ? await prefetched.clips
          : await getClipsForProject(project.id)
        if (clips.length === 0) {
          throw new Error(
            `Nothing to back up — this project has no ${kind === 'voice' ? 'recordings' : 'clips'}.`,
          )
        }
        const audio = usePrefetch
          ? await prefetched.audio
          : await getProjectAudio(project.id)
        // Voice notes travel with the audio so a restore is complete.
        const body = kind === 'video' ? '' : ((await getProjectText(project.id))?.body ?? '')
        const backup = serializeProject(project, clips, audio, body)
        const filename = projectBackupFilename(project.name)
        const sizeLabel = formatBytes(backup.size)
        // Android's share sheet fails (often silently) on very large files —
        // route big backups straight to a download instead.
        if (backup.size > SHARE_BACKUP_LIMIT_BYTES) {
          await downloadBlob(backup, filename)
          notice =
            `Backup (${sizeLabel}) saved to your downloads — too large for the share sheet. ` +
            'Drag the file onto this app to restore it.'
        } else {
          const outcome = await shareOrDownload(backup, filename)
          if (outcome !== 'cancelled') {
            notice = `Backup (${sizeLabel}) saved. Drag the file onto this app to restore it.`
          }
        }
      } catch (err) {
        error = err instanceof Error ? err.message : 'Could not create the backup'
      } finally {
        busy = false
        void handle.update()
      }
    })()
  }

  return () => {
    if (!data) {
      // Load failure must not leave a silent blank screen.
      return error ? (
        <div className="screen home-screen">
          <div className="error-banner">{error}</div>
        </div>
      ) : null
    }
    const { projects, countsByKind, storage, exportCacheBytes } = data

    const severity = storage ? storageSeverity(storage.ratio) : 'ok'
    const oldestProject = projects[0] ?? null
    const limit = maxProjectsFor(kind)
    const atLimit = projects.length >= limit
    const empty = EMPTY_COPY[kind]

    return (
      <div className="screen home-screen">
        {/* On mobile, the visible brand/LCP hero lives in index.html
            (#boot-hero) so first paint is not gated on JS — this block is a
            layout spacer there. Desktop hides #boot-hero and shows this. */}
        <div
          className="home-hero home-hero-spacer"
          aria-hidden={typeof window !== 'undefined' && window.matchMedia('(max-width: 719px)').matches}
        >
          <div className="home-hero-art">
            {/* The mark links out to the OKB site. New tab deliberately: this
                is an installable PWA, and navigating the app window away to a
                marketing site is a one-way door on some platforms. */}
            <a
              className="brand-hero-link"
              href="https://o-kb.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="OKB home page"
            >
              <BrandMark
                size={96}
                className="brand-hero-art"
                variant="camera"
                priority={
                  typeof window !== 'undefined' && window.matchMedia('(max-width: 719px)').matches
                }
              />
            </a>
          </div>
          <h1 className="brand">
            OKB <span>Studio</span>
          </h1>
          <p className="lede">Record, dictate or write — then publish to a feed.</p>
        </div>

        {showLayoutToggle ? (
          <button
            type="button"
            className="home-corner home-corner-left btn-icon"
            aria-label={layout === 'wide' ? 'Switch to phone layout' : 'Switch to wide layout'}
            title={layout === 'wide' ? 'Phone layout' : 'Wide layout'}
            mix={on('click', () => {
              layout = toggleLayoutMode()
              void handle.update()
            })}
          >
            {layout === 'wide' ? <IconPhone /> : <IconWide />}
          </button>
        ) : null}

        <a
          className="home-corner home-corner-right btn-icon"
          href="/settings"
          aria-label="Settings"
        >
          <IconSettings />
        </a>

        {error ? <div className="error-banner">{error}</div> : null}
        {notice ? <p className="home-notice">{notice}</p> : null}

        {storage && severity !== 'ok' ? (
          <div
            className={`storage-banner${severity === 'critical' ? ' is-critical' : ''}`}
            role="alert"
          >
            <strong>
              Device storage {formatStoragePercent(storage.ratio)} full
              {severity === 'critical' ? ' — recordings may start failing' : ''}
            </strong>
            <span>
              {formatBytes(storage.usedBytes)} of {formatBytes(storage.quotaBytes)} used.
              {oldestProject
                ? ` Free space fast: delete an old project (⋯ on “${oldestProject.name}”, then Delete).`
                : ' Free space by clearing other site data or files on this device.'}
            </span>
            {exportCacheBytes > 0 ? (
              <button
                type="button"
                className="btn btn-secondary storage-banner-action"
                disabled={busy}
                mix={on('click', onClearExportCache)}
              >
                Clear cached exports ({formatBytes(exportCacheBytes)})
              </button>
            ) : null}
          </div>
        ) : null}

        <nav className="kind-tabs" role="tablist" aria-label="Project type">
          {PROJECT_KINDS.map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={tab === kind}
              className={`kind-tab${tab === kind ? ' is-active' : ''}`}
              mix={on('click', () => selectKind(tab))}
            >
              {PROJECT_KIND_LABELS[tab]}
              {countsByKind[tab] > 0 ? (
                <span className="kind-tab-count">{countsByKind[tab]}</span>
              ) : null}
            </button>
          ))}
        </nav>

        <section className="project-slots" aria-label={`${PROJECT_KIND_LABELS[kind]} projects`}>
          {projects.map((project) => (
            <article
              key={project.id}
              className={`project-slot filled is-${kind}${
                project.posterThumb ? ' has-poster' : ''
              }`}
            >
              {project.posterThumb ? (
                <BlobImage
                  blob={project.posterThumb}
                  className="slot-poster"
                  alt=""
                  aria-hidden="true"
                  draggable={false}
                />
              ) : null}
              <div className="slot-fade" aria-hidden="true" />
              <a className="slot-open" href={`/project/${project.id}`}>
                <span className="slot-number">{PROJECT_KIND_LABELS[kind]}</span>
                <strong>{project.name}</strong>
                <small>
                  {kind === 'text' ? (
                    <>
                      {project.wordCount} word{project.wordCount === 1 ? '' : 's'}
                      {project.textPreview ? ` · ${project.textPreview}` : ''}
                    </>
                  ) : (
                    <>
                      {project.clipCount} {kind === 'voice' ? 'take' : 'clip'}
                      {project.clipCount === 1 ? '' : 's'} · {formatDuration(project.durationMs)}
                    </>
                  )}
                </small>
              </a>
              <button
                type="button"
                className="slot-options"
                aria-label={`Options for ${project.name}`}
                mix={on('click', () => {
                  // Video backups read clips + music; the other kinds have
                  // nothing worth prefetching for the share-sheet gesture.
                  prefetchedClips =
                    kind === 'video'
                      ? {
                          projectId: project.id,
                          clips: getClipsForProject(project.id),
                          audio: getProjectAudio(project.id),
                        }
                      : null
                  menuProject = project
                  void handle.update()
                })}
              >
                <IconMore />
              </button>
            </article>
          ))}

          {atLimit ? (
            <p className={`project-slot empty locked is-${kind}`} role="note">
              <strong>{PROJECT_KIND_LABELS[kind]} tab is full</strong>
              <small>{`Delete one of your ${limit} projects to start another.`}</small>
            </p>
          ) : (
            <button
              type="button"
              className={`project-slot empty is-${kind}`}
              disabled={busy}
              mix={on('click', openNewProject)}
            >
              <span className="slot-plus" aria-hidden="true">
                <IconPlus size={26} />
              </span>
              <strong>{empty.title}</strong>
              <small>{empty.hint}</small>
            </button>
          )}
        </section>

        {/* Restore lives outside the kind tabs on purpose: a backup carries its
            own kind, so which tab is showing has nothing to do with it. */}
        <div className="home-restore">
          <input
            type="file"
            className="visually-hidden"
            accept={BACKUP_FILE_ACCEPT}
            aria-hidden="true"
            tabIndex={-1}
            mix={[
              ref((node) => {
                backupInputRef.current = node as HTMLInputElement
              }),
              on('change', (event) => {
                const input = event.currentTarget as HTMLInputElement
                // Copy before clearing: FileList is live and empties with value=''.
                const file = input.files?.[0] ?? null
                input.value = ''
                if (file) restoreFromBackup(file)
              }),
            ]}
          />
          <button
            type="button"
            className="home-restore-btn"
            disabled={restoring !== null}
            mix={on('click', () => backupInputRef.current?.click())}
          >
            {restoring ?? 'Restore a project from a backup'}
          </button>
          {restoreError ? (
            <p className="home-restore-error" role="alert">
              {restoreError}
            </p>
          ) : (
            <p className="home-restore-hint muted">
              Opens a <code>.okbvideo</code> file as a new project — including one
              exported from another device.
            </p>
          )}
        </div>

        <p className="home-privacy">
          <span>Your work stays on this device until you publish.</span>
          {storage ? <StorageMeter storage={storage} /> : null}
        </p>

        {menuProject ? (
          <HomeOptionsSheet
            projectName={menuProject.name}
            canBackup={kind !== 'text'}
            onClose={() => {
              menuProject = null
              void handle.update()
            }}
            onOpen={() => {
              const id = menuProject!.id
              menuProject = null
              void handle.update()
              navigate(`/project/${id}`)
            }}
            onRename={() => {
              renaming = menuProject
              menuProject = null
              void handle.update()
            }}
            onBackup={() => {
              const project = menuProject!
              menuProject = null
              void handle.update()
              backupProject(project)
            }}
            onDelete={() => {
              deleting = menuProject
              menuProject = null
              void handle.update()
            }}
          />
        ) : null}

        {renaming ? (
          <RenameSheet
            key={renaming.id}
            initialName={renaming.name}
            onClose={() => {
              renaming = null
              void handle.update()
            }}
            onSave={async (name) => {
              await renameProject(renaming!.id, name)
              refresh()
            }}
          />
        ) : null}

        {deleting ? (
          <ConfirmSheet
            title={kind === 'text' ? 'Delete note?' : 'Delete project?'}
            message={`Delete “${deleting.name}”${
              kind === 'video'
                ? ' and all its clips'
                : kind === 'voice'
                  ? ' and all its recordings'
                  : ''
            }? This can’t be undone.`}
            confirmLabel="Delete"
            onClose={() => {
              deleting = null
              void handle.update()
            }}
            onConfirm={async () => {
              await deleteProject(deleting!.id)
              refresh()
            }}
          />
        ) : null}

      </div>
    )
  }
}
